import { useState, useEffect, useCallback } from 'react';
import type { Job, JobFilters, JobsResponse } from '../types';
import { useAuthenticatedApi } from '@/hooks/useAuthenticatedApi';

interface UseJobBoardReturn {
  jobs: Job[];
  loading: boolean;
  error: string | null;
  hasMore: boolean;
  totalCount: number;
  fetchJobs: (filters?: JobFilters) => Promise<void>;
  refreshJobs: () => Promise<void>;
  loadMoreJobs: () => Promise<void>;
}

export const useJobBoard = (initialFilters: JobFilters = {}): UseJobBoardReturn => {
  const [jobs, setJobs] = useState<Job[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [currentFilters, setCurrentFilters] = useState<JobFilters>(initialFilters);
  const [currentPage, setCurrentPage] = useState(1);
  const { invokeFunction } = useAuthenticatedApi();

  const fetchJobs = useCallback(async (filters: JobFilters = {}) => {
    setLoading(true);
    setError(null);
    
    try {
      const requestFilters = {
        page: filters.page || 1,
        limit: filters.limit || 20,
        ...filters
      };

      const response = await invokeFunction('job-board-api', {
        action: 'list_jobs',
        filters: requestFilters
      });

      if (!response.success) {
        throw new Error(response.error || 'Failed to fetch jobs');
      }

      const jobsResponse = response.data as JobsResponse;

      setJobs(jobsResponse.jobs);
      setTotalCount(jobsResponse.total);
      setHasMore(jobsResponse.has_more);
      setCurrentFilters(filters);
      setCurrentPage(jobsResponse.page);
      
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch jobs');
      console.error('Job fetch error:', err);
      
      // Fallback empty state
      setJobs([]);
      setTotalCount(0);
      setHasMore(false);
    } finally {
      setLoading(false);
    }
  }, [invokeFunction]);

  const refreshJobs = useCallback(async () => {
    setCurrentPage(1);
    await fetchJobs({ ...currentFilters, page: 1 });
  }, [currentFilters, fetchJobs]);

  const loadMoreJobs = useCallback(async () => {
    if (!hasMore || loading) return;
    
    const nextPage = currentPage + 1;
    await fetchJobs({ ...currentFilters, page: nextPage });
  }, [currentFilters, currentPage, hasMore, loading, fetchJobs]);

  // Initial fetch
  useEffect(() => {
    fetchJobs(initialFilters);
  }, [fetchJobs, initialFilters]);

  return {
    jobs,
    loading,
    error,
    hasMore,
    totalCount,
    fetchJobs,
    refreshJobs,
    loadMoreJobs
  };
};